import { FieldTypes } from '../../constants/FieldTypes'
import { EditorDraft, type Descendant, type EditorVersion } from '../../editor/types'
import { DeliveryLocation } from '../assessments/assessment'
import { FileDBValue } from '../data'
import { AttachmentShortcut, FieldFromType } from '../forms'
import { WithMetadata } from '../shared'
import { WithId } from '../utils'

export type TemplateAttachmentPlacementType = 'inline' | 'full-page'

export interface InlineAttachmentPlacement {
  type: 'inline'
  pageIndex: number
  orientation: {
    position: [number, number]
    scale: [number, number]
    rotation: number
  }
}

export interface FullPagePlacement {
  type: 'full-page'
  afterPageIndex: number
}

export type TemplateAttachmentPlacement = InlineAttachmentPlacement | FullPagePlacement

export const isInlineFilePlacement = (
  placement: TemplateAttachmentPlacement,
): placement is InlineAttachmentPlacement => placement.type === 'inline'

export type TemplateAttachmentType = 'file' | 'input'

interface BaseAttachment<
  Placement extends TemplateAttachmentPlacement = TemplateAttachmentPlacement,
> {
  id: string
  placement: Placement
}

export interface FileTemplateAttachment<
  Placement extends TemplateAttachmentPlacement = TemplateAttachmentPlacement,
> extends BaseAttachment<Placement> {
  type: 'file'
  file: FileDBValue
}
export type ExternalPdfFieldType =
  | FieldTypes.TEXT
  | FieldTypes.TEXTAREA
  | FieldTypes.DATE
  | FieldTypes.SIGNATURE
  | FieldTypes.INITIALS
  | FieldTypes.FILE
  | FieldTypes.DROPDOWN

export interface InputTemplateAttachment<
  Placement extends TemplateAttachmentPlacement = TemplateAttachmentPlacement,
> extends BaseAttachment<Placement> {
  type: 'input'
  field: FieldFromType<ExternalPdfFieldType>
  // Radio (DROPDOWN) inputs resize like data attachments: an explicit width/height box plus an
  // editable font size (rem) that drives the rendered input size. Other input types resize by scale
  // and leave these undefined.
  width?: number
  height?: number
  fontSize?: number
}

export interface DataTemplateAttachment<
  Placement extends TemplateAttachmentPlacement = TemplateAttachmentPlacement,
> extends BaseAttachment<Placement> {
  type: 'data'
  height: number
  width: number
  shortcut: AttachmentShortcut
  value?: string | FileDBValue | null
}

export type ShapeType = 'rectangle' | 'square' | 'oval' | 'circle'

export interface ShapeTemplateAttachment<
  Placement extends TemplateAttachmentPlacement = TemplateAttachmentPlacement,
> extends BaseAttachment<Placement> {
  type: 'shape'
  shapeType: ShapeType
  width: number
  height: number
  fillColor?: string
  borderColor?: string
  borderWidth?: number
}

export type TemplateAttachment<
  Placement extends TemplateAttachmentPlacement = TemplateAttachmentPlacement,
> =
  | FileTemplateAttachment<Placement>
  | InputTemplateAttachment<Placement>
  | DataTemplateAttachment<Placement>
  | ShapeTemplateAttachment<Placement>

export const isFileTemplateAttachment = <Placement extends TemplateAttachmentPlacement>(
  attachment: TemplateAttachment,
): attachment is FileTemplateAttachment<Placement> => attachment.type === 'file'

export const isAttachedInput = <Placement extends TemplateAttachmentPlacement>(
  attachment: TemplateAttachment,
): attachment is InputTemplateAttachment<Placement> => attachment.type === 'input'

export const isDataTemplateAttachment = <Placement extends TemplateAttachmentPlacement>(
  attachment: TemplateAttachment,
): attachment is DataTemplateAttachment<Placement> => attachment.type === 'data'

export const isShapeTemplateAttachment = <Placement extends TemplateAttachmentPlacement>(
  attachment: TemplateAttachment,
): attachment is ShapeTemplateAttachment<Placement> => attachment.type === 'shape'

export const isInlineAttachment = (
  attachment: TemplateAttachment,
): attachment is TemplateAttachment<InlineAttachmentPlacement> =>
  isInlinePlacement(attachment.placement)

export const isInlinePlacement = (
  placement: TemplateAttachmentPlacement,
): placement is InlineAttachmentPlacement => placement.type === 'inline'

export interface TemplateExternalPdf {
  baseFile: FileDBValue
  attachments: Record<string, TemplateAttachment>
}

interface AutoDisplayConditionBase {
  type:
    | 'none'
    | 'all'
    | 'medicaid'
    | 'non-medicaid'
    | 'insurer'
    | 'insurance-plan'
    | 'self-pay'
    | 'delivery-location'
}

export interface BasicAutoDisplayCondition extends AutoDisplayConditionBase {
  type: 'all' | 'medicaid' | 'non-medicaid' | 'self-pay'
}

export interface InsurerAutoDisplayCondition extends AutoDisplayConditionBase {
  type: 'insurer'
  insurerId: string
}

export interface InsurancePlanAutoDisplayCondition extends AutoDisplayConditionBase {
  type: 'insurance-plan'
  insurerId: string
  insurancePlanId: string
}

export interface DeliveryLocationAutoDisplayCondition extends AutoDisplayConditionBase {
  type: 'delivery-location'
  deliveryLocation: DeliveryLocation
}

export type AutoDisplayCondition =
  | BasicAutoDisplayCondition
  | InsurerAutoDisplayCondition
  | InsurancePlanAutoDisplayCondition
  | DeliveryLocationAutoDisplayCondition

// const basicAutoDisplayConditionTypes: BasicAutoDisplayCondition['type'][] = [
//   'all',
//   'medicaid',
//   'non-medicaid',
//   'self-pay',
// ]
// export const isBasicAutoDisplayCondition = (
//   condition: AutoDisplayCondition,
// ): condition is BasicAutoDisplayCondition =>
//   basicAutoDisplayConditionTypes.includes(condition.type as BasicAutoDisplayCondition['type'])

// export const isInsurerAutoDisplayCondition = (
//   condition: AutoDisplayCondition,
// ): condition is InsurerAutoDisplayCondition => condition.type === 'insurer'

export interface Template {
  // id: string
  name: string
  rank: number
  templateText?: Descendant[]
  editorVersion?: EditorVersion
  isExternalPdf?: boolean
  externalPdf?: TemplateExternalPdf | null
}

export interface ConsentFormTemplate extends Template {
  autoDisplay?: AutoDisplayCondition[] | null
  canBeCopiedToPractice?: boolean | null
  isInternal?: boolean | null
}

// Blueprint for a practice contract. HB super-admins author these; providers sign instances.
export interface PracticeContractTemplate extends Template {
  isInternal?: boolean | null
}

export type TemplateKey =
  | 'assessments'
  | 'authorizations'
  | 'authAppeals'
  | 'authInstructions'
  | 'invoiceAndReceipt'
  | 'claims'
  | 'clinicals'
  | 'consentForm'
  | 'practiceContract'

export type TemplateCollectionId =
  // assessment templates are stored in templates collection
  | 'templates'
  | 'authTemplates'
  | 'authAppealsTemplates'
  | 'authInstructionsTemplates'
  | 'invoicesReceiptsTemplates'
  | 'claimTemplates'
  | 'clinicalsTemplates'
  | 'consentFormTemplates'
  | 'practiceContractTemplates'

export interface TemplateKeyToCollectionId {
  assessments: 'templates'
  authorizations: 'authTemplates'
  authAppeals: 'authAppealsTemplates'
  authInstructions: 'authInstructionsTemplates'
  invoiceAndReceipt: 'invoicesReceiptsTemplates'
  claims: 'claimTemplates'
  clinicals: 'clinicalsTemplates'
  consentForm: 'consentFormTemplates'
  practiceContract: 'practiceContractTemplates'
}

export type TemplateEditorDraft = EditorDraft<
  WithMetadata<{
    templateType: TemplateKey
    templateId: TemplateId | null
  }>
>

export interface TemplateId {
  templateId: string
  practiceId: string | null
}

export interface RequiredTemplateInfo extends TemplateId {
  name: string
  conditions: AutoDisplayCondition[]
}

export interface MissingConsentForm {
  form: WithId<ConsentFormTemplate>
  conditions: AutoDisplayCondition[]
  practiceId: string | null
}
